import React, {
	useEffect,
	useRef,
	useState
} from "react";
import Heading from "@/components/elements/Heading";
import Modal from "@/components/Modal";
import api from "@/services/api";
import { uploadFile, triggerFileUpload, clearFileInput } from "@/services/fileUpload";
import { InertiaLink } from "@inertiajs/inertia-react";

/**
 * InputList component that manages product variations (parts list) with Excel import/export
 * Handles thickness calculations, TL position calculations, and dynamic form management
 * Features real-time validation, debounced updates, and keyboard shortcuts for efficiency
 *
 * @param {Object} props - Component props
 * @param {Array} props.variations - Array of product variations/parts
 * @param {Function} props.setVariations - Function to update variations array
 * @param {Object} props.errors - Validation errors object
 * @param {Object} props.product - Product configuration data
 * @param {string} props.saveVariationUrl - API endpoint to save individual variations
 * @param {string} props.uploadExcel - API endpoint for Excel file upload
 * @param {string} props.createUrl - API endpoint to create new variations
 * @param {Function} props.setNoRows - Function to control "no rows" modal state
 * @param {boolean} props.noRows - Whether to show "no rows" modal (default: false)
 * @param {Object} props.warnings - Validation warnings object
 * @param {Function} props.setWarnings - Function to update warnings
 * @param {Function} props.gotoNext - Function to proceed to next step
 * @param {string} props.updateVariationsUrl - API endpoint to update all variations
 */
const InputList = ({
    variations,
    setVariations,
    errors,
    product,
    saveVariationUrl,
    uploadExcel,
    createUrl,
    setNoRows,
    noRows = false,
    warnings,
    setWarnings,
    gotoNext,
    updateVariationsUrl,
}) => {

    // Refs for file input and focus management
    const excelref = useRef(null);
    const firstInput = useRef(null);

    // State management for debouncing, calculations, and component lifecycle
    const [typingTimeout, setTimingTimeout] = useState(0);
    const [thickness, setthickness] = useState(0);
    const [tlpos, setTlpos] = useState(0);
    const [firstLoad, setFirstLoad] = useState(true);

    // Calculate whether automatic thickness/TL position calculation is possible
    const calculateThickness = isThinknessCalculationPossible();
    const calculateTlPos = isCalculateTLPostPossible();

    /**
     * Update thickness value with input sanitization
     * Removes non-digit characters to ensure numeric input
     *
     * @param {string} value - Raw input value
     */
    function updateThickness(value) {
        let newVal = value.replace(/\D/g, ""); // Remove non-digits
        setthickness(newVal);
    }

    /**
     * Prevent decimal point and comma input in number fields
     * Blocks period (46) and comma (44) character codes
     *
     * @param {KeyboardEvent} e - The keypress event
     */
    function checkKey(e) {
        e = e || window.event;
        if (e.charCode === 44 || e.charCode === 46) {
            e.preventDefault();
        }
    }

    /**
     * Handle Tab key press to create new variation
     * Creates new variation with current thickness and TL position values
     * Focuses the first input after creation for continuous data entry
     *
     * @param {KeyboardEvent} event - The keydown event
     * @param {HTMLElement} target - The target element (unused)
     */
    function tabFunction(event, target) {
        if (event.keyCode === 9) { // Tab key
            event.preventDefault();
            let data = { TL: tlpos, DIKTE: thickness };
            api.post(createUrl, data).then((newvariation) => {
                setVariations([...variations, newvariation.data]);
                firstInput.current.select(); // Focus first input for next entry
            });
        }
    }

    /**
     * Determine if automatic thickness calculation is possible
     * Checks if product layers don't contain PU (polyurethane) materials
     * PU materials require manual thickness input
     *
     * @returns {boolean} Whether automatic calculation is possible
     */
    function isThinknessCalculationPossible() {
        if (product.products.hasOwnProperty("IS1") && product.products.IS1) {
            return !product.products.IS1.alias.includes("PU");
        }
        if (product.products.hasOwnProperty("IS2") && product.products.IS2) {
            return !product.products.IS2.alias.includes("PU");
        }
        return true;
    }

    /**
     * Determine if automatic TL position calculation is possible
     * Requires TL (core layer) to exist and no PU materials in insulation layers
     *
     * @returns {boolean} Whether automatic TL position calculation is possible
     */
    function isCalculateTLPostPossible() {
        if (product.products.hasOwnProperty("TL") && product.products.TL) {
            if (
                product.products.hasOwnProperty("IS1") &&
                product.products.IS1
            ) {
                return !product.products.IS1.alias.includes("PU");
            }
            if (
                product.products.hasOwnProperty("IS2") &&
                product.products.IS2
            ) {
                return !product.products.IS2.alias.includes("PU");
            }
        }
        return false;
    }

    /**
     * Close the "no rows" modal and hide it
     *
     * @param {Event} e - The click event
     */
    function setNoRowsClick(e) {
        if (e) {
            e.preventDefault();
        }
        setNoRows(false);
    }

    /**
     * Add a new variation to the list
     * Creates new variation with current thickness and TL position
     * Automatically focuses the first input for immediate data entry
     *
     * @param {Event} e - The click event
     */
    function addNewVariation(e) {
        setNoRows(false);
        if (e) {
            e.preventDefault();
        }
        let data = { TL: tlpos, DIKTE: thickness };
        api.post(createUrl, data).then((newvariation) => {
            setVariations([...variations, newvariation.data]);
            firstInput.current.select();
        });
    }

    /**
     * Update thickness and TL position properties for all variations
     * Used when global thickness or TL position values change
     *
     * @param {number} thickness_var - New thickness value
     * @param {number} tlpos_var - New TL position value
     * @returns {Array} Updated variations array
     */
    function updateVariationProps(thickness_var, tlpos_var) {
        return variations.map((variation) => {
            let vr = { ...variation };
            vr.properties.DIKTE = thickness_var;
            vr.properties.TL_POS = tlpos_var;
            return vr;
        });
    }

    /**
     * Initialize component on first load
     * Calculates thickness and TL position, updates all variations
     * Sets up initial state from product properties if calculation not possible
     */
    useEffect(() => {
        let newvariations = updateVariationProps(
            calcThickness(),
            calcTLPosition(),
        );

        // Set manual thickness if automatic calculation not possible
        if (
            !calculateThickness &&
            product &&
            product.properties &&
            product.properties.tickness &&
            product.properties.tickness > 0
        ) {
            setthickness(product.properties.tickness);
        } else {
            calcThickness();
            calcTLPosition();
        }

        // Update server with initial calculations
        api.put(updateVariationsUrl, { variations: newvariations });
        setVariations(newvariations);
        setFirstLoad(false);
    }, []);

    /**
     * Update all variations when thickness or TL position changes
     * Runs after initial load to sync changes with server
     */
    useEffect(() => {
        if (!firstLoad) {
            let newvariations = updateVariationProps(thickness, tlpos);
            api.put(updateVariationsUrl, { variations: newvariations });
            setVariations(newvariations);
        }
    }, [thickness, tlpos]);

    /**
     * Handle Excel file upload
     * Uploads file with current thickness and TL position data
     * Replaces current variations with uploaded data
     *
     * @param {FileList} files - Selected files from file input
     */
    const handleExcelUpload = (files) => {
        let data = { TL: calcTLPosition(), DIKTE: calcThickness() };

        uploadFile(uploadExcel, files[0], data).then((response) => {
            setVariations(response.data);
            clearFileInput(excelref);
        });
    };

    /**
     * Trigger file upload dialog
     *
     * @param {Event} e - The click event
     */
    function uploadExcelFile(e) {
        triggerFileUpload(e, excelref);
    }

    /**
     * Update a specific property of a variation with debouncing
     * Immediately updates UI, then saves to server after delay
     *
     * @param {string} key - Property key to update
     * @param {string|number} value - New value
     * @param {Object} variationoption - Variation object to update
     * @param {boolean} number - Whether to parse value as integer
     */
    function updateValue(key, value, variationoption, number) {
        const vars = [...variations];
        let variation = vars.find((x) => x.id === variationoption.id);
        variation.properties[key] = number ? parseInt(value) : value;
        setVariations(vars);

        // Debounced save to server
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        setTimingTimeout(
            setTimeout(() => {
                updateVariation(variation);
            }, 300),
        );
    }

    /**
     * Save individual variation to server
     *
     * @param {Object} variation - Variation object to save
     */
    function updateVariation(variation) {
        api.put(saveVariationUrl, variation);
    }

    /**
     * Delete a variation from the list
     * Removes from server and updates local state
     *
     * @param {Event} e - The click event
     * @param {Object} variation - Variation to delete
     * @param {number} key - Index in variations array
     */
    function deleteVariation(e, variation, key) {
        e.preventDefault();
        const vars = [...variations];
        api.delete(`/api/product/variation/${variation.variation_id}`).then(() => {
            vars.splice(key, 1);
            setVariations(vars);
        });
    }

    /**
     * Select all text in input field on focus
     * Improves user experience for quick value replacement
     *
     * @param {FocusEvent} event - The focus event
     */
    function handleFocus(event) {
        if (event) {
            event.target.select();
        }
    }

    /**
     * Calculate total thickness from all product layers
     * Sums Z-dimension sizes of all product components
     * Falls back to existing variation or product property if calculation not possible
     *
     * @returns {number} Calculated or retrieved thickness value
     */
    const calcThickness = () => {
        let thickness = 0;
        if (calculateThickness) {
            // Sum thickness of all layers: Bottom, Inside, Core, Insulation1, Insulation2
            ["BU", "BI", "TL", "IS1", "IS2"].forEach((value, index) => {
                if (
                    product.products.hasOwnProperty(value) &&
                    product.products[value]
                ) {
                    thickness =
                        thickness + parseInt(product.products[value].zdimsize);
                }
            });
        } else {
            // Use existing values if calculation not possible
            if (variations.length > 0) {
                thickness = variations[0].properties.DIKTE;
            } else if (
                product &&
                product.properties &&
                product.properties.tickness
            ) {
                thickness = product.properties.tickness;
            }
        }
        setthickness(thickness);
        return thickness;
    };

    /**
     * Calculate TL (core layer) position from outside
     * Sums thickness of outer layers (BU + IS1) to determine core position
     * Falls back to existing variation value if calculation not possible
     *
     * @returns {number} Calculated or retrieved TL position value
     */
    const calcTLPosition = () => {
        let tlpos = 0;
        if (calculateTlPos) {
            // Calculate from outside layers
            if (product.products.hasOwnProperty("BU") && product.products.BU) {
                tlpos = tlpos + parseInt(product.products.BU.zdimsize);
            }
            if (
                product.products.hasOwnProperty("IS1") &&
                product.products.IS1
            ) {
                tlpos = tlpos + parseInt(product.products.IS1.zdimsize);
            }
        } else {
            // Use existing value if calculation not possible
            if (variations.length > 0) {
                tlpos = variations[0].properties.TL_POS;
            }
        }
        setTlpos(tlpos);
        return tlpos;
    };

    return (
        <div
            className={`contentoptions ingaveoptions col-md-9 d-flex flex-column`}
        >
            {/* Header with action buttons */}
            <Heading heading="Stuklijst" hideProduct={true}>
                <div>
                    <a
                        name="Upload excel"
                        href="#"
                        className="btn-orange excelhide mt-0"
                        onClick={uploadExcelFile}
                    >
                        Upload excel
                    </a>
                    <a
                        name="Nieuwe regel"
                        href="#"
                        className="btn-orange mr-0 mt-0"
                        onClick={addNewVariation}
                    >
                        Nieuwe regel
                    </a>
                    {/* Hidden file input for Excel upload */}
                    <input
                        type="file"
                        onChange={(e) => handleExcelUpload(e.target.files)}
                        className="d-none"
                        ref={excelref}
                        accept="text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    />
                </div>
            </Heading>

            {/* Global settings for thickness and TL position */}
            <div className="row mb-4 mt-2">
                <div className="col-lg-6 d-flex align-items-center mb-lg-0 mb-md-3">
                    <p
                        style={{
                            fontSize: 14,
                            flexShrink: 0,
                            marginRight: 8,
                            marginBottom: 0,
                        }}
                    >
                        Dikte (in mm):
                    </p>
                    {calculateThickness ? (
                        // Display calculated thickness (read-only)
                        thickness
                    ) : (
                        // Manual thickness input
                        <input
                            type="number"
                            value={thickness || ""}
                            onChange={(e) => updateThickness(e.target.value)}
                            onKeyPress={checkKey}
                            className={`form-control ${errors && errors.algemeen && errors.algemeen.includes("DIKTE") ? "is-invalid" : ""}`}
                        />
                    )}
                </div>
                <div className="col-lg-6 d-flex align-items-center ">
                    <p
                        style={{
                            fontSize: 14,
                            flexShrink: 0,
                            marginRight: 8,
                            marginBottom: 0,
                        }}
                    >
                        Positie kernplaat (in mm):
                    </p>
                    {product.properties.hasOwnProperty("TL") &&
                    product.properties.TL !== 0 &&
                    !calculateThickness ? (
                        // Manual TL position input
                        <input
                            type="number"
                            value={tlpos || ""}
                            onChange={(e) => setTlpos(e.target.value)}
                            className={`form-control ${errors && errors.algemeen && errors.algemeen.includes("TL_POS") ? "is-invalid" : ""}`}
                        />
                    ) : calculateThickness && product.properties.TL !== 0 ? (
                        // Display calculated TL position
                        tlpos
                    ) : (
                        "NVT"
                    )}
                </div>
            </div>

            {/* Variations table */}
            <table className="table tablecustom">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Aantal</th>
                        <th>Breedte (mm)</th>
                        <th>Hoogte (mm)</th>
                        <th>Merk (max 30 tekens)</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {variations.map((variation, key) => {
                        return (
                            <tr
                                key={key}
                                className={
                                    variation.id in errors ? "error" : ""
                                }
                            >
                                <td className="align-middle">{key + 1}</td>
                                {/* Quantity input */}
                                <td>
                                    <input
                                        type="number"
                                        value={variation.properties.AANTAL || ""}
                                        autoFocus={true}
                                        ref={
                                            key + 1 === variations.length
                                                ? firstInput
                                                : null
                                        }
                                        onFocus={handleFocus}
                                        onChange={(e) =>
                                            updateValue(
                                                "AANTAL",
                                                e.target.value,
                                                variation,
                                                true,
                                            )
                                        }
                                        className={`form-control ${variation.id in errors && errors[variation.id].includes("AANTAL") ? "is-invalid" : ""}`}
                                    />
                                </td>
                                {/* Width input */}
                                <td>
                                    <input
                                        type="number"
                                        value={variation.properties.BREEDTE || ""}
                                        onFocus={handleFocus}
                                        onChange={(e) =>
                                            updateValue(
                                                "BREEDTE",
                                                e.target.value,
                                                variation,
                                                true,
                                            )
                                        }
                                        className={`form-control ${variation.id in errors && errors[variation.id].includes("BREEDTE") ? "is-invalid" : ""}`}
                                    />
                                </td>
                                {/* Height input */}
                                <td>
                                    <input
                                        type="number"
                                        value={variation.properties.HOOGTE || ""}
                                        onFocus={handleFocus}
                                        onChange={(e) =>
                                            updateValue(
                                                "HOOGTE",
                                                e.target.value,
                                                variation,
                                                true,
                                            )
                                        }
                                        className={`form-control ${variation.id in errors && errors[variation.id].includes("HOOGTE") ? "is-invalid" : ""}`}
                                    />
                                </td>
                                {/* Description input with Tab key handler for last row */}
                                <td>
                                    <input
                                        type="text"
                                        value={
                                            variation.properties.OMSCHRIJVING || ""
                                        }
                                        tabIndex={0}
                                        maxLength={30}
                                        onKeyDown={
                                            key + 1 === variations.length
                                                ? (e) =>
                                                      tabFunction(e, e.target)
                                                : () => undefined
                                        }
                                        onChange={(e) =>
                                            updateValue(
                                                "OMSCHRIJVING",
                                                e.target.value,
                                                variation,
                                            )
                                        }
                                        className="form-control"
                                    />
                                </td>
                                {/* Delete button */}
                                <td className="align-middle">
                                    <div className="d-flex align-items-center justify-content-center h-100">
                                        <a
                                            name={"Verwijder"}
                                            href="#"
                                            onClick={(e) =>
                                                deleteVariation(e, variation, key)
                                            }
                                            tabIndex="-1"
                                            className="mr-2"
                                        >
                                            <svg
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                height={15}
                                                className="ingaveoptions__icon"
                                            >
                                                <path
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clipRule="evenodd"
                                                    fillRule="evenodd"
                                                />
                                            </svg>
                                        </a>
                                        <InertiaLink
                                            href={`/${product.session_id}/${product.product_id}/${variation.variation_id}/cutouts`}
                                            name="Cutouts"
                                        >
                                            <svg
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                height={15}
                                                className="ingaveoptions__icon"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            </svg>
                                        </InertiaLink>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Modal for prompting user to add rows when none exist */}
            <Modal
                open={noRows}
                title={"Verwijderen"}
                content={
                    "Je hebt geen regels aan de stuklijst toegevoegd. Wil je er 1 toevoegen?"
                }
                savetitle="Voeg regel toe"
                canceltitle="Annuleer"
                buttonclass="btn-orange"
                savefunction={addNewVariation}
                updateOpen={(e) => setNoRowsClick(e)}
            />

            {/* Modal for size validation warnings */}
            <Modal
                open={warnings?.sizes}
                title={"Waarschuwing"}
                content={
                    <span style={{ whiteSpace: 'pre-line' }}>
                        De volgende regels zijn groter dan de bruto plaatmaat:
                        <br />
                        <br />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: (warnings.sizes ?? []).join("<br/>")
                            }}
                        />
                        <br />
                        Wil je toch je bestelling voortzetten?
                    </span>
                }
                savetitle="Volgende"
                canceltitle="Terug"
                buttonclass="btn-orange"
                savefunction={gotoNext}
                updateOpen={() => setWarnings({})}
            />
        </div>
    );
};

export default InputList;
